"use client"

import { useEffect, useState } from "react"
import { resolveStack, formatResolved, decodeReactErrorMessage } from "@/lib/debug/errorResolution"

/** Root error boundary — replaces the entire root layout, so the app's global
 *  CSS (Tailwind) is NOT loaded here; all styling must be inline. We still
 *  de-minify: decode the React error code into readable text and resolve the
 *  minified stack against deployed source maps, so even a root-layout crash
 *  never shows a bare "Minified React error #185". */
export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
    const [resolvedStack, setResolvedStack] = useState<string | null>(null)
    const decoded = decodeReactErrorMessage(error?.message)

    useEffect(() => {
        console.error("[Cardinal global error boundary]", error)
        let cancelled = false
        if (error?.stack) {
            resolveStack(error.stack)
                .then(frames => { if (!cancelled) setResolvedStack(frames.map(formatResolved).join("\n")) })
                .catch(() => {})
        }
        return () => { cancelled = true }
    }, [error])

    const details = [
        `message: ${decoded}`,
        error?.message && error.message !== decoded ? `raw message: ${error.message}` : null,
        error?.digest ? `digest: ${error.digest}` : null,
        error?.stack ? `stack:\n${error.stack}` : null,
        resolvedStack ? `resolved stack:\n${resolvedStack}` : null,
    ].filter(Boolean).join("\n\n")

    return (
        <html lang="en">
            <body style={{ margin: 0, fontFamily: "Inter, system-ui, -apple-system, sans-serif" }}>
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#fff",
                        padding: "24px",
                        boxSizing: "border-box",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            padding: "20px",
                            maxWidth: "640px",
                            width: "100%",
                            border: "1px solid #FECACA",
                            borderRadius: "12px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                            <span style={{ fontSize: "24px" }}>😵‍💫</span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <p style={{ fontSize: "14px", fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
                                    Something went wrong
                                </p>
                                <p style={{ fontSize: "13px", color: "#605A5A", margin: "4px 0 0", wordBreak: "break-word" }}>
                                    {decoded}
                                </p>
                            </div>
                        </div>
                        {resolvedStack ? (
                            <pre
                                style={{
                                    maxHeight: "256px",
                                    overflow: "auto",
                                    whiteSpace: "pre-wrap",
                                    border: "1px solid #E5E5E5",
                                    background: "#FAFAFA",
                                    borderRadius: "6px",
                                    padding: "8px",
                                    fontSize: "11px",
                                    lineHeight: 1.4,
                                    color: "#605A5A",
                                    margin: 0,
                                }}
                            >
                                {resolvedStack}
                            </pre>
                        ) : null}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                            <button
                                type="button"
                                onClick={() => { try { void navigator.clipboard.writeText(details) } catch { /* ignore */ } }}
                                style={{
                                    padding: "6px 12px",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "#605A5A",
                                    background: "#fff",
                                    border: "1px solid #E5E5E5",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                }}
                            >
                                Copy details
                            </button>
                            <button
                                type="button"
                                onClick={reset}
                                style={{
                                    padding: "6px 16px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#1a1a1a",
                                    background: "#FAFAFA",
                                    border: "1px solid #E5E5E5",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                }}
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            </body>
        </html>
    )
}
